import { useState } from 'react'
import { useAutoSaveForm } from '../../../components/forms/useAutoSaveForm'
import FormHeader from '../../../components/forms/FormHeader'
import { api } from '../../../lib/api'
import { toast } from '../../../lib/toast'
import Input from '../../../components/ui/Input'
import Button from '../../../components/ui/Button'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'

interface Tweet {
  url: string
  oembed_html: string
  author?: string
  fetched_at: string
}

interface SettingsState {
  rotation_interval: number
  loop: boolean
  show: boolean
  rich: boolean
  always_show: boolean
  display_duration: number
}

interface Props {
  overlayId: string
  initial: Record<string, unknown>
}

export default function TwitterFeedForm({ overlayId, initial }: Props) {
  const [tweets, setTweets] = useState<Tweet[]>(((initial.tweets as Tweet[]) || []))
  const [newURL, setNewURL] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmRemoveIdx, setConfirmRemoveIdx] = useState<number | null>(null)

  // Settings auto-save
  const settings = useAutoSaveForm<SettingsState>({
    initial: {
      rotation_interval: Number(initial.rotation_interval) || 8,
      loop: initial.loop !== false,
      show: initial.show !== false,
      rich: initial.rich !== false,
      // always_show default true (geçici): yeni overlay'lerde test/preview için sürekli göster.
      // Production'da false yapılırsa "yeni tweet geldiğinde N sn göster, sonra gizle" davranışı.
      always_show: initial.always_show !== false,
      display_duration: Number(initial.display_duration) || 12,
    },
    onSave: async (s) => {
      await api(`/api/overlays/${overlayId}/state`, {
        method: 'PATCH',
        body: JSON.stringify(s),
      })
    },
  })

  async function addTweet(e: React.FormEvent) {
    e.preventDefault()
    if (!newURL.trim()) return
    setBusy(true)
    try {
      const updated = await api<{ state: { tweets: Tweet[] } }>(
        `/api/overlays/${overlayId}/tweets`,
        { method: 'POST', body: JSON.stringify({ url: newURL.trim() }) },
      )
      setTweets(updated.state.tweets || [])
      setNewURL('')
      toast.success('Tweet eklendi')
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function removeTweet(idx: number) {
    try {
      const updated = await api<{ state: { tweets: Tweet[] } }>(
        `/api/overlays/${overlayId}/tweets/${idx}`,
        { method: 'DELETE' },
      )
      setTweets(updated.state.tweets || [])
      toast.success('Tweet silindi')
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  return (
    <div className="space-y-5">
      <FormHeader
        title="Twitter Feed"
        subtitle="Tweet rotation overlay'i"
        status={settings.status}
        savedAt={settings.savedAt}
        error={settings.error}
        onUndo={settings.canUndo ? settings.undo : undefined}
      />

      <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-elevated)] p-4 space-y-3">
        <h3 className="text-sm font-semibold">Tweet Ekle</h3>
        <form onSubmit={addTweet} className="flex gap-2">
          <Input
            placeholder="https://twitter.com/user/status/..."
            value={newURL}
            onChange={(e) => setNewURL(e.target.value)}
            className="flex-1"
          />
          <Button type="submit" variant="primary" disabled={busy || !newURL.trim()}>
            Ekle
          </Button>
        </form>
        <div className="text-xs text-[var(--color-text-muted)]">
          oembed otomatik çekilir; en üste yeni eklenir.
        </div>
      </section>

      <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-elevated)] p-4">
        <h3 className="text-sm font-semibold mb-2">Listede ({tweets.length})</h3>
        {tweets.length === 0 && (
          <div className="text-xs text-[var(--color-text-subtle)]">Henüz tweet yok.</div>
        )}
        <ul className="space-y-1 max-h-80 overflow-y-auto">
          {tweets.map((t, i) => (
            <li key={t.url} className="flex items-center gap-2 p-1.5 border-b last:border-0 border-[var(--color-border)]">
              <span className="text-xs font-mono text-[var(--color-text-subtle)] w-6">#{i}</span>
              <span className="text-xs flex-1 truncate">
                {t.author && <span className="text-[var(--color-accent-hover)]">@{t.author}</span>}{' '}
                <span className="text-[var(--color-text-muted)]">{t.url}</span>
              </span>
              <Button variant="ghost" size="sm" onClick={() => setConfirmRemoveIdx(i)}>
                ×
              </Button>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-md border border-[var(--color-border)] bg-[var(--color-elevated)] p-4 space-y-3">
        <h3 className="text-sm font-semibold">Görüntüleme Modu</h3>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={settings.draft.always_show}
            onChange={(e) => settings.patch({ always_show: e.target.checked })}
            className="accent-[var(--color-accent)] mt-0.5"
          />
          <span>
            <span className="font-medium">Sürekli göster (test modu)</span>
            <span className="block text-xs text-[var(--color-text-muted)] mt-0.5">
              Açık: tweet'ler rotation interval ile sürekli ekranda döner (tasarım önizlemesi).
              Kapalı: yeni tweet geldiğinde N saniye göster, sonra gizle (yayın modu).
            </span>
          </span>
        </label>

        {settings.draft.always_show ? (
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1.5">
              Rotation süresi (saniye)
            </label>
            <Input
              type="number"
              min={3}
              max={60}
              value={settings.draft.rotation_interval}
              onChange={(e) => settings.patch({ rotation_interval: Number(e.target.value) })}
              className="w-32"
            />
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-1.5">
              Tweet ekranda kalma süresi (saniye)
            </label>
            <Input
              type="number"
              min={3}
              max={60}
              value={settings.draft.display_duration}
              onChange={(e) => settings.patch({ display_duration: Number(e.target.value) })}
              className="w-32"
            />
          </div>
        )}

        {settings.draft.always_show && (
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={settings.draft.loop}
              onChange={(e) => settings.patch({ loop: e.target.checked })}
              className="accent-[var(--color-accent)]"
            />
            Loop (sona gelince başa dön). Kapalıysa son tweet'ten sonra ekran gizlenir.
          </label>
        )}

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={settings.draft.show}
            onChange={(e) => settings.patch({ show: e.target.checked })}
            className="accent-[var(--color-accent)]"
          />
          Yayında göster
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={settings.draft.rich}
            onChange={(e) => settings.patch({ rich: e.target.checked })}
            className="accent-[var(--color-accent)]"
          />
          Zengin görünüm (foto/video — Twitter widgets.js, ilk yüklemede ~200ms)
        </label>
      </section>

      <ConfirmDialog
        open={confirmRemoveIdx !== null}
        onClose={() => setConfirmRemoveIdx(null)}
        onConfirm={async () => {
          if (confirmRemoveIdx === null) return
          await removeTweet(confirmRemoveIdx)
        }}
        title="Tweet sil?"
        description={confirmRemoveIdx !== null ? tweets[confirmRemoveIdx]?.url : ''}
        confirmLabel="Sil"
        destructive
      />
    </div>
  )
}
